import { useApi, useToaster } from "@shopexperts/shared";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { computed, type Ref, toValue } from "vue";

interface UseExpertBadgesManagementOptions {
  expertId: Ref<number> | number;
  search?: Ref<string> | string;
}

export function useExpertBadgesManagement(options: UseExpertBadgesManagementOptions) {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const expertIdValue = computed(() => toValue(options.expertId));
  const searchValue = computed(() => toValue(options.search ?? ""));

  const allBadgesQuery = useInfiniteQuery({
    queryKey: computed(() => ["admin", "badges", "all", { search: searchValue.value }]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.badges.listBadges({
        query: {
          limit: 50,
          cursor: pageParam,
          search: searchValue.value || undefined,
          status: "active",
        },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const allBadges = computed(() => {
    return allBadgesQuery.data.value?.pages.flatMap((page) => page.data) || [];
  });

  const syncBadges = useMutation({
    mutationKey: ["admin", "experts", "syncBadges"],
    mutationFn: async (badgeIds: number[]) => {
      return await api.admin.badges.syncBadgesToExpert({
        params: { expertId: expertIdValue.value },
        body: { badge_ids: badgeIds },
      });
    },
    onSuccess: () => {
      toaster.create({ title: "Badges updated successfully", type: "success" });
      queryClient.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "admin" &&
          q.queryKey[1] === "expert" &&
          String(q.queryKey[2]) === String(expertIdValue.value),
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "experts"] });
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to update badges: ${error.message}`, type: "error" });
    },
  });

  return {
    allBadges,
    isLoadingBadges: allBadgesQuery.isLoading,
    syncBadges,
    isSyncing: syncBadges.isPending,
  };
}
